import SEO from "@/components/SEO";
import { PageHero, Section, SectionHead, FAQ } from "@/components/sections";
import { Reveal } from "@/components/Reveal";
import { CTALink } from "@/components/ui-kit";
import LeadForm from "@/components/LeadForm";
import { Activity, BookOpen, Zap, ShieldCheck } from "lucide-react";

const features = [
  { icon: Activity, name: "Direct equity execution", desc: "Buy and hold shares directly in your demat account, with research and market access via our Sharekhan partnership." },
  { icon: BookOpen, name: "Research-backed ideas", desc: "Access curated research and coverage to inform decisions — potential opportunities framed with their risks." },
  { icon: Zap, name: "Trading support", desc: "Platforms and support for active participants, from delivery to intraday, aligned to your risk appetite." },
  { icon: ShieldCheck, name: "Guided, not gambled", desc: "We keep direct equity as one considered sleeve of a wider plan — never a substitute for it." },
];

const faq = [
  { q: "Who is direct equity suitable for?", a: "Investors comfortable with higher volatility and single-stock risk, ideally as part of a diversified plan. Equity markets are subject to market risk; returns are potential, not assured." },
  { q: "How does the Sharekhan partnership work?", a: "We partner with Sharekhan for execution, platforms and research access, while advice and planning stay with Rao Investment Solutions." },
];

export default function DirectEquity() {
  return (
    <div>
      <SEO path="/products/direct-equity" title="Direct Equity & Trading" description="Direct equity investing and trading in partnership with Sharekhan — research access and execution, guided within a wider plan by Rao Investment Solutions." faq={faq} />
      <PageHero
        label="Products"
        title="Direct Equity & Trading"
        intro="For investors who want to own companies directly — with research, execution and support, in partnership with Sharekhan."
        breadcrumbs={[{ name: "Home", to: "/" }, { name: "Products" }, { name: "Direct Equity & Trading" }]}
      />

      <Section className="!pt-0">
        <div className="grid md:grid-cols-2 gap-6">
          {features.map((f, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="h-full bg-white p-8 md:p-10 border border-navy/10 hover:shadow-lg transition-shadow">
                <f.icon className="w-8 h-8 text-orange" strokeWidth={1.25} />
                <h3 className="mt-6 font-serif text-2xl text-navy">{f.name}</h3>
                <p className="mt-3 font-sans text-base text-slate leading-relaxed">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section cream>
        <div className="flex flex-col md:flex-row md:items-center gap-6 justify-between p-10 bg-navy text-white">
          <div>
            <span className="font-sans text-xs uppercase tracking-[0.24em] text-orange">Partner</span>
            <h3 className="mt-3 font-serif text-2xl md:text-3xl">Execution & research via Sharekhan</h3>
          </div>
          <CTALink to="/contact" variant="secondaryLight" data-testid="equity-contact-cta">Open a conversation</CTALink>
        </div>
      </Section>

      <Section>
        <div className="max-w-3xl"><SectionHead label="FAQ" title="Before you trade" /><FAQ items={faq} /></div>
      </Section>

      <Section dark>
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          <SectionHead label="Next step" title="Add a considered equity sleeve." dark intro="We'll make sure direct equity fits your plan, not the other way round." />
          <div className="bg-white/5 border border-white/10 p-8 md:p-10"><LeadForm dark interest="Direct Equity & Trading" title="Request access" /></div>
        </div>
      </Section>
    </div>
  );
}
